import React, { useState } from 'react';
import { CSSObject, Theme } from '@emotion/react';
import { ModalComponent } from 'components';
import { IEvent, IPartner } from 'types';
import { EventQuery } from 'queries';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { mobile } from 'styles/media';
import PreviewEnrollForm from './PreviewEnrollForm';

dayjs.locale('ko');

type TabType = 'detail' | 'notice' | 'map' | 'partner';

const TAB_LABELS: Record<TabType, string> = {
  detail: '상세정보',
  notice: '공지사항',
  map: '위치안내',
  partner: '협업기관',
};

const STATUS_BADGE: Record<
  IEvent['eventStatus'],
  { label: string; active: boolean }
> = {
  RECRUITING: { label: '모집 중', active: true },
  REVIEW: { label: '리뷰 중', active: false },
  RECRUITMENT_CLOSED: { label: '모집 완료', active: false },
  COMPLETED: { label: '봉사 종료', active: false },
  CANCELLED: { label: '취소된 봉사', active: false },
};

const ENROLL_TEXT: Record<IEvent['eventStatus'], string> = {
  RECRUITING: '신청하기',
  REVIEW: '오픈 준비중',
  RECRUITMENT_CLOSED: '모집 완료된 봉사입니다',
  COMPLETED: '마감된 봉사입니다',
  CANCELLED: '취소된 봉사입니다',
};

interface Props {
  event: IEvent;
  onClose: () => void;
}

function PreviewModal({ event, onClose }: Props) {
  const [view, setView] = useState<'detail' | 'enroll'>('detail');
  const [activeTab, setActiveTab] = useState<TabType>('detail');
  const { data: partners } = EventQuery.useGetPartner(String(event.id));

  const badge = STATUS_BADGE[event.eventStatus];
  const fmt = (d: string) => dayjs(d).format('YYYY.MM.DD (ddd)');

  return (
    <ModalComponent.Portal>
      <ModalComponent.Background>
        <div css={phoneWrap}>
          <div css={previewLabel}>
            <div />
            <button type="button" onClick={onClose} css={closeBtn}>
              ✕
            </button>
          </div>

          <div css={phoneFrame}>
            {/* status bar */}
            <div css={statusBar}>
              <span css={{ fontSize: 12, fontWeight: 600 }}>9:41</span>
              <div css={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span css={{ fontSize: 10 }}>●●●</span>
              </div>
            </div>

            {/* scrollable content */}
            <div css={scrollArea}>
              {view === 'enroll' && (
                <PreviewEnrollForm
                  event={event}
                  onBack={() => setView('detail')}
                />
              )}
              {view === 'detail' && (
                <>
                  {/* thumbnail */}
                  {event.mainImageUrl ? (
                    <div css={thumbWrap}>
                      <div css={thumbBlur(event.mainImageUrl)} />
                      <img src={event.mainImageUrl} alt="" css={thumbImg} />
                    </div>
                  ) : (
                    <div css={thumbPlaceholder}>이미지 없음</div>
                  )}

                  {/* title section */}
                  <div css={titleSection}>
                    <span css={statusBadgeStyle(badge.active)}>
                      {badge.label}
                    </span>
                    <p css={titleText}>{event.title}</p>
                    <p css={orgText}>{event.organizationName}</p>
                  </div>

                  {/* info card */}
                  <div css={infoCard}>
                    <InfoRow
                      label="진행 일시"
                      value={`${fmt(event.startDate)} ~ ${fmt(event.endDate)}`}
                    />
                    <InfoRow
                      label="신청 기간"
                      value={`${fmt(event.enrollStartDate)} ~ ${fmt(event.enrollEndDate)}`}
                    />
                    <InfoRow label="진행 장소" value={event.locationName} />
                  </div>

                  {/* tab bar */}
                  <div css={tabBar}>
                    {(Object.keys(TAB_LABELS) as TabType[]).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        css={tabItem(tab === activeTab)}
                        onClick={() => setActiveTab(tab)}
                      >
                        {TAB_LABELS[tab]}
                      </button>
                    ))}
                  </div>

                  {/* tab content */}
                  <div css={tabContent}>
                    {activeTab === 'detail' &&
                      (event.description ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: event.description,
                          }}
                          css={htmlContent}
                        />
                      ) : (
                        <p css={emptyText}>상세 내용이 없습니다.</p>
                      ))}

                    {activeTab === 'notice' &&
                      (event.notice ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: event.notice }}
                          css={htmlContent}
                        />
                      ) : (
                        <p css={emptyText}>유의사항이 없습니다.</p>
                      ))}

                    {activeTab === 'map' && (
                      <div>
                        <MapInfoRow
                          label="봉사 장소"
                          value={event.locationName}
                        />
                        <MapInfoRow
                          label="집결 장소"
                          value="참여자에 한해 별도 안내 (공지방 입장 필요)"
                        />
                        <div css={mapPlaceholder}>
                          <span css={{ fontSize: 24 }}>🗺</span>
                          <span
                            css={{ fontSize: 12, color: '#888', marginTop: 4 }}
                          >
                            {event.locationName}
                          </span>
                          <span
                            css={{ fontSize: 10, color: '#aaa' }}
                          >{`${event.latitude}, ${event.longitude}`}</span>
                        </div>
                      </div>
                    )}

                    {activeTab === 'partner' && (
                      <div>
                        <p css={partnerTitle}>협업 기관</p>
                        {(partners as IPartner[] | undefined)?.length ? (
                          <div css={partnerGrid}>
                            {(partners as IPartner[]).map((p) => (
                              <div key={p.id} css={partnerItem}>
                                <img
                                  src={p.thumbnail}
                                  alt={p.name}
                                  css={partnerImg}
                                />
                                <span css={partnerName}>{p.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p css={emptyText}>등록된 협업기관이 없습니다.</p>
                        )}
                      </div>
                    )}

                    <div css={{ height: 24 }} />
                  </div>
                </>
              )}
            </div>

            {/* bottom enrollment button */}
            <div css={bottomArea}>
              <div css={bottomGradient} />
              {view === 'detail' ? (
                <button
                  type="button"
                  css={enrollBtn}
                  onClick={() => setView('enroll')}
                >
                  신청하기
                </button>
              ) : (
                <button type="button" css={enrollBtnDisabled} disabled>
                  신청 완료
                </button>
              )}
            </div>
          </div>
        </div>
      </ModalComponent.Background>
    </ModalComponent.Portal>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div css={infoRow}>
      <span css={infoLabel}>{label}</span>
      <span css={infoValue}>{value}</span>
    </div>
  );
}

function MapInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div css={mapInfoRow}>
      <span css={infoLabel}>{label}</span>
      <span css={infoValue}>{value}</span>
    </div>
  );
}

/* ────────────── styles ────────────── */

const phoneWrap: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  [mobile]: {
    width: '100%',
    height: '100%',
  },
};

const previewLabel: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  padding: '0 4px',
};

const closeBtn: CSSObject = {
  background: 'none',
  border: 'none',
  color: '#fff',
  fontSize: 18,
  cursor: 'pointer',
  padding: '2px 6px',
  lineHeight: 1,
};

const phoneFrame: CSSObject = {
  width: 390,
  height: 720,
  border: '2px solid #222',
  borderRadius: 40,
  background: '#fff',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
  position: 'relative',
  [mobile]: {
    width: '100%',
    height: 'auto',
    flex: 1,
    minHeight: 0,
    border: 'none',
    borderRadius: 16,
  },
};

const statusBar: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 24px 6px',
  flexShrink: 0,
  background: '#fff',
  [mobile]: {
    display: 'none',
  },
};

const scrollArea: CSSObject = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': { width: 0 },
};

const thumbWrap: CSSObject = {
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  overflow: 'hidden',
  flexShrink: 0,
};

const thumbBlur = (url: string): CSSObject => ({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${url})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(20px)',
  transform: 'scale(1.1)',
});

const thumbImg: CSSObject = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const thumbPlaceholder: CSSObject = {
  width: '100%',
  aspectRatio: '1 / 1',
  background: '#f5f5f5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  color: '#aaa',
  flexShrink: 0,
};

const titleSection: CSSObject = {
  padding: '16px 16px 8px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const statusBadgeStyle = (active: boolean): CSSObject => ({
  display: 'inline-flex',
  alignSelf: 'flex-start',
  padding: '4px 8px',
  borderRadius: 2,
  fontSize: 11,
  fontWeight: 600,
  background: active ? '#111' : '#e9ecef',
  color: active ? '#fff' : '#868e96',
  border: `1px solid ${active ? '#111' : '#dee2e6'}`,
});

const titleText: CSSObject = {
  fontSize: 18,
  fontWeight: 700,
  color: '#212529',
  lineHeight: 1.4,
};

const orgText: CSSObject = {
  fontSize: 13,
  fontWeight: 600,
  color: '#868e96',
};

const infoCard: CSSObject = {
  margin: '0 16px 12px',
  background: '#f8f9fa',
  border: '1px solid #f1f3f5',
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const infoRow: CSSObject = {
  display: 'flex',
  gap: 12,
};

const infoLabel: CSSObject = {
  fontSize: 12,
  fontWeight: 500,
  color: '#868e96',
  flexShrink: 0,
  width: 56,
};

const infoValue: CSSObject = {
  fontSize: 12,
  fontWeight: 500,
  color: '#343a40',
  lineHeight: 1.5,
};

const tabBar: CSSObject = {
  display: 'flex',
  borderBottom: '1px solid #f1f3f5',
  flexShrink: 0,
};

const tabItem = (active: boolean): CSSObject => ({
  flex: 1,
  height: 44,
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid #212529' : '2px solid transparent',
  fontSize: 13,
  fontWeight: active ? 600 : 400,
  color: active ? '#212529' : '#868e96',
  cursor: 'pointer',
  transition: 'all 0.15s',
});

const tabContent: CSSObject = {
  padding: 16,
  minHeight: 120,
};

const htmlContent: CSSObject = {
  fontSize: 13,
  lineHeight: 1.7,
  color: '#343a40',
  img: { maxWidth: '100%' },
  p: { margin: '4px 0' },
};

const emptyText: CSSObject = {
  fontSize: 13,
  color: '#adb5bd',
  textAlign: 'center',
  padding: '24px 0',
};

const mapInfoRow: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginBottom: 16,
};

const mapPlaceholder: CSSObject = {
  width: '100%',
  height: 160,
  background: '#e9ecef',
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
};

const partnerTitle: CSSObject = {
  fontSize: 14,
  fontWeight: 700,
  color: '#212529',
  marginBottom: 12,
};

const partnerGrid: CSSObject = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
};

const partnerItem: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  width: 64,
};

const partnerImg: CSSObject = {
  width: 58,
  height: 30,
  objectFit: 'contain',
  background: '#f8f9fa',
  borderRadius: 4,
  border: '1px solid #f1f3f5',
};

const partnerName: CSSObject = {
  fontSize: 10,
  color: '#868e96',
  textAlign: 'center',
  wordBreak: 'keep-all',
};

const bottomArea: CSSObject = {
  flexShrink: 0,
  padding: '0 16px 20px',
  background: '#fff',
  position: 'relative',
};

const bottomGradient: CSSObject = {
  position: 'absolute',
  top: -24,
  left: 0,
  right: 0,
  height: 24,
  background: 'linear-gradient(to bottom, transparent, #fff)',
  pointerEvents: 'none',
};

const enrollBtn: CSSObject = {
  width: '100%',
  height: 56,
  borderRadius: 13,
  border: 'none',
  background: '#212529',
  color: '#fff',
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
};

const enrollBtnDisabled: CSSObject = {
  width: '100%',
  height: 56,
  borderRadius: 13,
  border: 'none',
  background: '#e9ecef',
  color: '#868e96',
  fontSize: 16,
  fontWeight: 600,
  cursor: 'default',
};

export default PreviewModal;
